import React, { createContext, useContext, useState, useCallback } from 'react';

interface PortalAuthContextValue {
  portalToken: string | null;
  portalLeadId: string | null;
  setPortalSession: (token: string, leadId: string) => void;
  clearPortalSession: () => void;
}

const PortalAuthContext = createContext<PortalAuthContextValue | null>(null);

export const PortalAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Read from sessionStorage on mount — persists across page refreshes in the same tab
  const [portalToken, setPortalToken] = useState<string | null>(
    () => sessionStorage.getItem('portalToken')
  );
  const [portalLeadId, setPortalLeadId] = useState<string | null>(
    () => sessionStorage.getItem('portalLeadId')
  );

  const setPortalSession = useCallback((token: string, leadId: string) => {
    sessionStorage.setItem('portalToken', token);
    sessionStorage.setItem('portalLeadId', leadId);
    setPortalToken(token);
    setPortalLeadId(leadId);
  }, []);

  const clearPortalSession = useCallback(() => {
    sessionStorage.removeItem('portalToken');
    sessionStorage.removeItem('portalLeadId');
    setPortalToken(null);
    setPortalLeadId(null);
  }, []);

  return (
    <PortalAuthContext.Provider value={{ portalToken, portalLeadId, setPortalSession, clearPortalSession }}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export const usePortal = (): PortalAuthContextValue => {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error('usePortal must be used inside PortalAuthProvider');
  return ctx;
};
